import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../../teacher_payments/teacher_payments.css';
import { is_permission_error } from '../../teacher_payments/format';
import { fetch_audit_list } from '../../teacher_payments/audit/api';
import DataTable from '../../teacher_payments/shared/data_table';
import LoadFailed from '../../teacher_payments/shared/load_failed';
import { SOURCES, append_source, list_params, merged_history } from './change_history_logic';

const COLUMNS = [
  { key: 'when', label: 'When (IST)', hideInCard: true },
  { key: 'who', label: 'Who' },
  {
    key: 'changes',
    label: 'Change',
    render: (r) => (
      <div>
        {r.changes.map((text) => (
          <div key={text}>{text}</div>
        ))}
      </div>
    )
  },
  { key: 'reason', label: 'Reason' }
];

const EMPTY = Object.fromEntries(SOURCES.map((s) => [s.key, { rows: [], next_before_id: null }]));

/*
 * PROD-7828 — every edit made from the Payment details cards for this teacher, newest first. Grant-only
 * like the cards: a 401/403 hides the section. `version` changes when a card saves, which reloads it.
 */
const ChangeHistory = ({ teacher_uuid, version = 0 }) => {
  const [sources, setSources] = useState(EMPTY);
  const [state, setState] = useState('loading'); // loading | ready | error | forbidden
  const [more_busy, setMoreBusy] = useState(false);
  const [more_error, setMoreError] = useState('');
  // Only the latest request may land: a save-triggered reload must not be overwritten by an older one.
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!teacher_uuid) return;
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    setMoreError('');
    Promise.all(SOURCES.map((s) => fetch_audit_list(list_params(teacher_uuid, s.entity_type))))
      .then((pages) => {
        if (mine !== seq.current) return;
        const next = {};
        SOURCES.forEach((s, i) => {
          next[s.key] = append_source(null, pages[i]);
        });
        setSources(next);
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        if (is_permission_error(e)) {
          setState('forbidden');
          return;
        }
        console.error('payment change history load failed', e);
        setState('error');
      });
  }, [teacher_uuid]);

  useEffect(() => {
    setSources(EMPTY);
    load();
  }, [load, version]);

  const load_more = () => {
    const pending = SOURCES.filter((s) => sources[s.key].next_before_id);
    if (!pending.length || more_busy) return;
    const mine = seq.current;
    setMoreBusy(true);
    setMoreError('');
    Promise.all(
      pending.map((s) =>
        fetch_audit_list(list_params(teacher_uuid, s.entity_type, sources[s.key].next_before_id))
      )
    )
      .then((pages) => {
        if (mine !== seq.current) return;
        setSources((prev) => {
          const next = { ...prev };
          pending.forEach((s, i) => {
            next[s.key] = append_source(prev[s.key], pages[i]);
          });
          return next;
        });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        console.error('payment change history load more failed', e);
        setMoreError('Could not load older changes.');
      })
      .finally(() => {
        if (mine === seq.current) setMoreBusy(false);
      });
  };

  if (!teacher_uuid || state === 'forbidden') return null;

  const { rows, has_more } = merged_history(sources);

  let body;
  if (state === 'error') {
    body = <LoadFailed text="Could not load the change history." on_retry={load} />;
  } else if (state === 'loading') {
    body = <div className="tp-empty">Loading change history…</div>;
  } else {
    body = (
      <>
        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => r.id}
          cardTitle={(r) => r.when}
          emptyText="No changes have been made from this page yet."
        />
        {has_more ? (
          <div className="tp-frow" style={{ padding: '8px 14px', margin: 0 }}>
            <button type="button" className="tp-btn-out" onClick={load_more} disabled={more_busy}>
              {more_busy ? 'Loading…' : 'Show older changes'}
            </button>
            {more_error ? <span className="tp-err">{more_error}</span> : null}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="tp-card" aria-label="Change history" style={{ marginTop: 12 }}>
      <div className="tp-card-hd">
        <div>
          <div className="tp-card-hd-title">Change history</div>
          <div className="tp-card-hd-sub">
            GST, Free Pass, profile live date and base rate edits, newest first
          </div>
        </div>
        <button type="button" className="tp-link" onClick={load} disabled={state === 'loading'}>
          Refresh
        </button>
      </div>
      {body}
    </section>
  );
};

export default ChangeHistory;
